import CustomButton from 'Components/CustomButton'
import DeleteDialog from 'Components/DeleteDialog'
import { WithLoader } from 'Components/WithLoader'
import { FaultReportUpdate } from 'Services/SalesManagement/Features/FaultReport/Components/FaultReportUpdate'
import { SaveFaultReportUpdateDialog } from 'Services/SalesManagement/Features/FaultReport/Components/SaveFaultReportUpdateDialog'
import {
  deleteFaultReportUpdate,
  type FaultReportUpdateShape,
  fetchFaultReportUpdates
} from 'Services/SalesManagement/Features/FaultReport/faultReportSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { Box, List, ListItem } from '@mui/material'
import Typography from '@mui/material/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { type FC, useState } from 'react'
import { t } from 'ttag'

const FaultReportUpdateList: FC<{
  faultReportId: number
  faultReportUpdates: FaultReportUpdateShape[]
  hasControlRights: boolean
  isNewInstance: boolean
  isLoading: boolean
}> = ({ faultReportId, faultReportUpdates, hasControlRights, isNewInstance, isLoading }) => {
  const dispatch = useAppDispatch()

  const { submitLoading, deleteLoading } = useTypedSelector(state => state.faultReport)

  const initialState: FaultReportUpdateShape = {
    id: null,
    description_fi: '',
    updated: null
  }

  const [selectedFaultReportUpdate, setSelectedFaultReportUpdate] =
    useState<FaultReportUpdateShape>(initialState)

  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)

  const handleEditClick = (selectedFaultReportUpdate: FaultReportUpdateShape) => {
    setSelectedFaultReportUpdate(selectedFaultReportUpdate)
    setIsSaveDialogOpen(true)
  }

  const handleDeleteClick = (selectedFaultReportUpdate: FaultReportUpdateShape) => {
    setSelectedFaultReportUpdate(selectedFaultReportUpdate)
    setIsDeleteDialogOpen(true)
  }

  const handleDelete = () => {
    const params = {
      faultReportId,
      id: selectedFaultReportUpdate.id as number
    }
    dispatch(deleteFaultReportUpdate(params))
      .then(unwrapResult)
      .then(() => {
        SnackbarUtils.success(t`Fault report update successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting fault report update.`)
      })
      .finally(() => {
        setIsDeleteDialogOpen(false)
        dispatch(
          fetchFaultReportUpdates({
            faultReportId
          })
        )
      })
  }

  const canControl = hasControlRights && !isNewInstance

  return (
    <>
      <DeleteDialog
        open={isDeleteDialogOpen}
        handleClose={() => setIsDeleteDialogOpen(false)}
        handleSubmit={handleDelete}
        loading={deleteLoading === 'pending'}
        title={t`Delete fault report update`}
      >
        <Typography component="span" color="text.primary">
          {t`Are you sure you want to delete the fault report update?`}
        </Typography>
      </DeleteDialog>
      <SaveFaultReportUpdateDialog
        faultReportId={faultReportId}
        data={selectedFaultReportUpdate}
        open={isSaveDialogOpen}
        loading={submitLoading === 'pending'}
        handleClose={() => setIsSaveDialogOpen(false)}
      />

      <Box mt={4}>
        <WithLoader loading={isLoading}>
          <Box>
            <Typography component="h2" variant="h2">{t`Fault report updates`}</Typography>
            <List>
              {faultReportUpdates.length > 0 ? (
                <>
                  {faultReportUpdates.map(faultReportUpdate => (
                    <FaultReportUpdate
                      key={faultReportUpdate.id}
                      faultReportUpdate={faultReportUpdate}
                      handleEdit={() => handleEditClick(faultReportUpdate)}
                      handleDelete={() => handleDeleteClick(faultReportUpdate)}
                      canControl={canControl}
                    />
                  ))}
                </>
              ) : (
                <ListItem sx={{ my: 1 }}>
                  <Typography color="text.secondary">{t`No fault report updates added`}</Typography>
                </ListItem>
              )}
            </List>
            <Box>
              {canControl && (
                <CustomButton
                  sx={{ mb: 1 }}
                  onClick={() => handleEditClick(initialState)}
                  size="small"
                  startIcon={<AddIcon />}
                >
                  {t`Create new update`}
                </CustomButton>
              )}
            </Box>
          </Box>
        </WithLoader>
      </Box>
    </>
  )
}

export { FaultReportUpdateList }
